<script setup lang="ts">
import { ref } from 'vue'
import { useEnvironmentStore } from '@/stores/environmentStore'
import { vOnClickOutside } from '@vueuse/components'
import { useToast } from 'vue-toast-notification'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toast = useToast({ position: 'bottom', queue: true, duration: 1000 })
const env = useEnvironmentStore()

const isVisible = ref(false)

function toggleSelect() {
  isVisible.value = !isVisible.value
}

function switchLanguage(newLocale: string) {
  toggleSelect()
  env.setLocale(newLocale).then(() => {
    let locale = t('locale.' + env.currentLocale)
    let msg = t('toast.language-changed', [locale])
    toast.info(msg, { position: 'bottom' })
  })
}

const forceClose = () => {
  isVisible.value = false
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    forceClose()
  }
}
</script>

<template>
  <div class="custom-select" v-on-click-outside="forceClose">
    <button
      class="select-button"
      @click="toggleSelect"
      @keydown="onKeydown"
      aria-haspopup="listbox"
      :aria-expanded="isVisible"
    >
      <span class="selected-value">
        <img :src="env.currentFlag" :alt="env.currentLocale + ' flag'" width="28" height="21" />
      </span>
      <span class="arrow"></span>
    </button>
    <ul
      class="select-dropdown"
      v-if="isVisible"
      role="listbox"
      @keydown="onKeydown"
      v-adjust-position
    >
      <li
        v-for="loc in env.supportedLocales"
        :key="loc.code"
        role="option"
        :aria-selected="env.currentLocale === loc.code"
      >
        <button
          @click="switchLanguage(loc.code)"
          class="item"
          :class="{ selected: env.currentLocale === loc.code }"
        >
          <img :src="loc.flag" :alt="loc.label + ' flag'" width="28" height="21" />
          {{ loc.label }}
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
/* Reset default styles on UL and LI */
ul,
li {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Container styling */
.custom-select {
  position: relative;
  display: inline-block;
  background-color: var(--color-app-background);
}

/* Select button styling */
.select-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-radius: 0.25rem;
  cursor: pointer;
  background-color: var(--color-app-background);
}

/* Selected value within the button */
.selected-value {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.5em 0.75em;
}

/* Dropdown styling using UL */
.select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  /* Use intrinsic sizing based on content */
  width: max-content;
  background-color: transparent;

  border-radius: 0.25rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  color: var(--color-text);
}

/* Styling for each item in the list */
.item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  width: 100%;
  background: var(--color-app-background);
  border: 2px solid var(--color-menu-divider-2);
  cursor: pointer;
  color: var(--color-text);
}

.item.selected {
  font-weight: 900;
}

/* Hover and focus states */
.item:hover,
.item:focus {
  background-color: var(--color-menu);
}

/* Optional arrow styling */
.arrow {
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #fff;
  transition: transform ease-in-out 0.3s;
}
</style>
